import { t } from "@lingui/macro";
import { CaretDownIcon, CaretUpIcon, MagicWandIcon } from "@phosphor-icons/react";
import { defaultSections } from "@reactive-resume/schema";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Textarea,
} from "@reactive-resume/ui";
import { useMemo, useState } from "react";

import { useToast } from "@/client/hooks/use-toast";
import { generateText } from "@/client/services/openai/generate";
import { useResumeStore } from "@/client/stores/resume";

type Props = {
  onGenerate: (text: string) => void;
  type: "summary" | "experience" | "skills";
  existingContext?: string;
};

export const AiGenerator = ({ onGenerate, type, existingContext = "" }: Props) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addonPrompt, setAddonPrompt] = useState("");
  const [count, setCount] = useState(10);
  const [isJobDescExpanded, setIsJobDescExpanded] = useState(false);

  const jobDescription = useResumeStore(
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    (state) => state.resume.data.sections.jobDescription ?? defaultSections.jobDescription,
  );

  // Combine all job description fields
  const jobDescriptionText = useMemo(() => {
    const parts = [];
    if (jobDescription.description) {
      parts.push(`${t`Job Description`}:\n${jobDescription.description}`);
    }
    if (jobDescription.skillsRequired)
      parts.push(`${t`Skills Required`}:\n${jobDescription.skillsRequired}`);
    if (jobDescription.responsibilities)
      parts.push(`${t`Responsibilities`}:\n${jobDescription.responsibilities}`);
    if (jobDescription.impact) parts.push(`${t`Impact`}:\n${jobDescription.impact}`);
    return parts.join("\n\n");
  }, [
    jobDescription.description,
    jobDescription.skillsRequired,
    jobDescription.responsibilities,
    jobDescription.impact,
  ]);

  const hasJobDescription = jobDescriptionText.trim().length > 0;

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const fullContext = [jobDescriptionText, addonPrompt].filter(Boolean).join("\n\n");
      const result = await generateText(fullContext, type, count, existingContext.trim());
      onGenerate(result);
      setOpen(false);
      setAddonPrompt("");
    } catch (error) {
      toast({
        variant: "error",
        title: t`Oops, something went wrong.`,
        description: (error as Error).message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" className="rounded-full">
          <MagicWandIcon />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <MagicWandIcon />
              {t`Generate using AI`}
            </div>
          </DialogTitle>
          <DialogDescription>
            {t`Customize your prompt and generate AI content for this section.`}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Job Description Section - View Button */}
          {hasJobDescription && (
            <div className="grid gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="w-fit"
                onClick={() => {
                  setIsJobDescExpanded(!isJobDescExpanded);
                }}
              >
                {isJobDescExpanded ? (
                  <>
                    <CaretUpIcon size={16} />
                    <span className="ml-2">{t`Hide Job Description`}</span>
                  </>
                ) : (
                  <>
                    <CaretDownIcon size={16} />
                    <span className="ml-2">{t`View Job Description`}</span>
                  </>
                )}
              </Button>
              {isJobDescExpanded && (
                <div className="max-h-[300px] overflow-y-auto rounded-md border bg-secondary/50 p-3 text-sm">
                  <pre className="whitespace-pre-wrap font-sans text-xs">{jobDescriptionText}</pre>
                </div>
              )}
            </div>
          )}

          {/* Addon Prompt Field */}
          <div className="grid gap-2">
            <Label htmlFor="addonPrompt">{t`Add-on Prompt`}</Label>
            <Textarea
              id="addonPrompt"
              placeholder={t`Generate the summary points relevant to my job experience and focus on the EVG technology`}
              value={addonPrompt}
              className="min-h-[120px]"
              onChange={(e) => {
                setAddonPrompt(e.target.value);
              }}
            />
          </div>

          {/* Number of Bullet Points */}
          {type !== "skills" && (
            <div className="grid gap-2">
              <Label htmlFor="count">{t`Number of Bullet Points`}</Label>
              <Input
                id="count"
                type="number"
                min={1}
                max={20}
                value={count}
                onChange={(e) => {
                  setCount(Number(e.target.value));
                }}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              setOpen(false);
            }}
          >
            {t`Cancel`}
          </Button>
          <Button disabled={loading} onClick={handleGenerate}>
            {loading ? t`Generating...` : t`Generate`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
